import React from 'react';
import type {LucideIcon} from 'lucide-react';
import {Circle} from '@uiw/react-color';
import {
    BoldIcon,
    ItalicIcon,
    TextAlignCenterIcon,
    TextAlignEndIcon,
    TextAlignStartIcon
} from 'lucide-react';
import CanvasController from './CanvasController.ts';
import {
    StickyNoteColors,
    StickyNoteDefaultFontFamily,
    StickyNoteTextAlign,
    StickyNoteTextSizes
} from './stickyNote.ts';
import {ToolButton, Toolbar, ToolbarDivider} from '../ds/ToolFrame.tsx';

interface StickyNoteToolbarProps {
    controlsDisabled: boolean;
    controller: CanvasController;
}

const textSizeOptions = [
    {label: 'XS', title: 'Extra small text', fontSize: StickyNoteTextSizes.xsmall},
    {label: 'S', title: 'Small text', fontSize: StickyNoteTextSizes.small},
    {label: 'M', title: 'Medium text', fontSize: StickyNoteTextSizes.medium},
    {label: 'L', title: 'Large text', fontSize: StickyNoteTextSizes.large}
];

const fontOptions = [
    {label: 'Poppins', value: StickyNoteDefaultFontFamily},
    {label: 'Arial', value: 'Arial'},
    {label: 'Times New Roman', value: 'Times New Roman'},
    {label: 'Courier New', value: 'Courier New'},
    {label: 'Quicksand', value: 'Quicksand'}
];

const textAlignOptions: {
    title: string;
    textAlign: StickyNoteTextAlign;
    icon: LucideIcon;
}[] = [
    {title: 'Align left', textAlign: 'left', icon: TextAlignStartIcon},
    {title: 'Align centre', textAlign: 'center', icon: TextAlignCenterIcon},
    {title: 'Align right', textAlign: 'right', icon: TextAlignEndIcon}
];

const StickyNoteToolbar: React.FC<StickyNoteToolbarProps> = ({
    controlsDisabled,
    controller
}) => {
    const note = controller.activeStickyNote;
    const currentColor = note?.color ?? controller.stickyNoteDefaults.fill;
    const currentFontSize =
        note?.fontSize ?? controller.stickyNoteDefaults.fontSize;
    const currentTextAlign =
        note?.textAlign ?? controller.stickyNoteDefaults.textAlign;
    const currentFontFamily =
        note?.fontFamily ?? controller.stickyNoteDefaults.fontFamily;
    const currentBold =
        note?.bold ?? controller.stickyNoteDefaults.fontWeight === 'bold';
    const currentItalic =
        note?.italic ?? controller.stickyNoteDefaults.fontStyle === 'italic';

    return (
        <Toolbar label="Sticky note" className="whiteboard-toolbar">
            <ToolButton
                label="Bold"
                icon={BoldIcon}
                selected={currentBold}
                onClick={() => controller.toggleStickyNoteBold()}
                disabled={controlsDisabled}
            />
            <ToolButton
                label="Italic"
                icon={ItalicIcon}
                selected={currentItalic}
                onClick={() => controller.toggleStickyNoteItalic()}
                disabled={controlsDisabled}
            />
            <select
                className="form-select form-select-sm mx-toolbar-select"
                aria-label="Font"
                value={currentFontFamily}
                onChange={e => controller.setStickyNoteFontFamily(e.target.value)}
                disabled={controlsDisabled}
            >
                {fontOptions.map(option => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
            <ToolbarDivider />
            {textSizeOptions.map(option => (
                <ToolButton
                    key={option.label}
                    label={option.title}
                    selected={currentFontSize === option.fontSize}
                    onClick={() => controller.setStickyNoteFontSize(option.fontSize)}
                    disabled={controlsDisabled}
                >
                    <span className="mx-toolbutton-text">{option.label}</span>
                </ToolButton>
            ))}
            <ToolbarDivider />
            {textAlignOptions.map(option => (
                <ToolButton
                    key={option.textAlign}
                    label={option.title}
                    icon={option.icon}
                    selected={currentTextAlign === option.textAlign}
                    onClick={() => controller.setStickyNoteTextAlign(option.textAlign)}
                    disabled={controlsDisabled}
                />
            ))}
            <ToolbarDivider />
            <div className="mx-toolbar-swatches" title="Sticky note colour">
                <Circle
                    colors={StickyNoteColors}
                    color={currentColor}
                    rectProps={{
                        style: {width: 18, height: 18, borderRadius: '50%'}
                    }}
                    style={{flexWrap: 'nowrap', gap: 2}}
                    onChange={color => {
                        if (!controlsDisabled) {
                            controller.setStickyNoteColor(color.hex);
                        }
                    }}
                />
            </div>
        </Toolbar>
    );
};

export default StickyNoteToolbar;
